import { Injectable } from '@angular/core';
import {
	BlindData,
	BlindSetup,
	BlindsValidity,
	BlindTypes,
	BlindValidityStatus,
	MaterialType,
	PartRules,
	PartRuleTranslationKey,
	PartsAvailability,
	PartValidationResult,
	PartValidationSeverity,
	SectionKeysEnum,
	Size,
} from '@root/app.interfaces';
import { ShareService } from '@core/services/share-data/share-data.service';
import { debounceTime, Subject } from 'rxjs';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { STORAGE_NAMES } from '@root/app.config';
import { TranslateService } from '@ngx-translate/core';
import { VariantService } from '@core/services/variant/variant.service';

const OPERATION_IDS = {
	springBalanced: 1,
	motorised: 2,
};

@Injectable({
	providedIn: 'root',
})
export class PartAvailabilityService {
	private blindUpdate: Subject<number> = new Subject<number>();
	private blindsValidity: BlindsValidity = {};
	private readonly isShop = this.variantService.isShop();

	private partRules: PartRules = {
		[SectionKeysEnum.Operation]: [
			{
				id: OPERATION_IDS.springBalanced, // Spring Balanced
				validate: this.springBalancedValidator,
				translation_key: 'availability_messages.spring_balanced',
			},
			{
				id: OPERATION_IDS.motorised, // Motorised
				validate: this.motorisedValidator,
				translation_key: {
					[BlindTypes.Outdoor]: 'availability_messages.motorised',
					[BlindTypes.Interior]: 'availability_messages.motorised_interior',
				},
			},
		],
		[SectionKeysEnum.Material]: [
			{
				id: 1, // Sunscreen mesh
				validate: this.sunscreenMeshValidator,
				translation_key: 'availability_messages.spring_balanced',
			},
		],
		[SectionKeysEnum.Size]: [
			{
				id: null,
				validate: this.sizeValidator,
				translation_key: 'availability_messages.size',
			},
		],
	};
	constructor(
		private shareService: ShareService,
		private sessionStorageService: SessionStorageService,
		private translateService: TranslateService,
		private variantService: VariantService,
	) {
		this.getBlindUpdate();
	}

	updateBlind(blindId: number): void {
		this.blindUpdate.next(blindId);
	}

	resetAvailability(): void {
		this.blindsValidity = {};
	}

	blindDeleted(blindId: number): void {
		if (this.blindsValidity[blindId]) {
			delete this.blindsValidity[blindId];
		}
	}

	checkBlindsValidityFromStorage(): void {
		this.resetAvailability();

		const blinds: BlindData[] = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		if (blinds.length > 0) {
			blinds.forEach((blind) => this.checkBlind(blind));
		}
	}

	allBlindsValid(): boolean {
		const blinds: BlindData[] = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		if (!blinds.length) {
			return true;
		}

		blinds.forEach((blind) => {
			this.updateBlindValidityStatus(blind, this.getAvailability(blind));
		});

		return !this.getBlindsOverallStatus().block;
	}

	private getBlindUpdate(): void {
		this.blindUpdate
			.asObservable()
			.pipe(debounceTime(200))
			.subscribe((blindId) => {
				const blind: BlindData = this.sessionStorageService.getBlindItemById(blindId, STORAGE_NAMES.zip_blind_data)?.[0];
				this.checkBlind(blind);
			});
	}

	private checkBlind(blind: BlindData): void {
		const { size, material } = blind?.setup;

		if (size && material) {
			const availability = this.getAvailability(blind);
			this.shareService.setPartAvailability({ blindId: blind.blind_id, availability });
			this.updateBlindValidityStatus(blind, availability);
		}
	}

	private getAvailability(blind: BlindData): PartsAvailability {
		const availability = {};
		Object.entries(this.partRules).forEach(([key, options]) => {
			availability[key] = options.map((option) => {
				const { severity, valid }: PartValidationResult = option.validate.call(this, blind);
				return {
					partId: option.id,
					status: severity,
					valid,
					message: this.getTranslation(option.translation_key, blind.type as BlindTypes),
				};
			});
		});

		return availability;
	}

	private updateBlindValidityStatus(blind: BlindData, availability: PartsAvailability): void {
		let hasBlock = false;
		let hasWarning = false;

		for (const [section, options] of Object.entries(availability)) {
			const currentPart = blind.setup[section];

			const matched = options.find((option) => option.partId === currentPart.id || (!option.partId && !currentPart.id));
			if (!matched) {
				continue;
			}

			if (matched.status === PartValidationSeverity.Block) {
				hasBlock = true;
				break;
			}

			if (matched.status === PartValidationSeverity.Warning) {
				hasWarning = true;
			}
		}

		this.blindsValidity[blind.blind_id] = {
			block: hasBlock,
			warning: hasWarning,
		};

		this.setBlindsValidityStatus(this.blindsValidity);
	}

	private getBlindsOverallStatus(): BlindValidityStatus {
		const values = Object.values(this.blindsValidity);

		const hasBlock = values.some((value) => value.block);
		const hasWarning = values.some((value) => value.warning);

		return {
			block: hasBlock,
			warning: hasWarning,
		};
	}

	private setBlindsValidityStatus(BlindsValidity: BlindsValidity): void {
		this.shareService.setBlindsValidityStatus(BlindsValidity);
	}

	private getTranslation(translationKey: PartRuleTranslationKey, blindType: BlindTypes): string | null {
		const key = typeof translationKey === 'string' ? translationKey : translationKey[blindType];
		const translation = this.translateService.instant(this.translateService.instant(key));
		return translation !== translationKey ? translation : null;
	}

	private springBalancedValidator({ setup }: BlindData): PartValidationResult {
		const isInvalid = this.isSunscreenMaterial(setup.material.type) && this.canUseSpringBalancedBySize(setup.size);
		return { valid: !isInvalid, severity: isInvalid ? PartValidationSeverity.Block : PartValidationSeverity.Allow };
	}

	private motorisedValidator(blind: BlindData): PartValidationResult {
		switch (blind.type) {
			case BlindTypes.Interior:
				return this.motorisedInteriorValidator(blind.setup);
			case BlindTypes.Outdoor:
				return this.motorisedOutdoorValidator(blind.setup);
			default:
				return { valid: true, severity: PartValidationSeverity.Allow };
		}
	}

	private motorisedInteriorValidator(setup: BlindSetup): PartValidationResult {
		const { width } = setup.size;
		const isInvalid = width >= 500 && width <= 700;
		return { valid: !isInvalid, severity: PartValidationSeverity.Warning };
	}

	private motorisedOutdoorValidator(setup: BlindSetup): PartValidationResult {
		const valid = setup.size.width > 800;
		return { valid, severity: valid ? PartValidationSeverity.Allow : PartValidationSeverity.Block };
	}

	private sunscreenMeshValidator({ setup }: BlindData): PartValidationResult {
		if (!this.isSunscreenMaterial(setup.material.type)) {
			return { valid: true, severity: PartValidationSeverity.Allow };
		}

		const isSpringBalancedSelected = setup.operation.id === OPERATION_IDS.springBalanced;
		const isInvalid = isSpringBalancedSelected && this.canUseSpringBalancedBySize(setup.size);
		return { valid: !isInvalid, severity: isInvalid ? PartValidationSeverity.Block : PartValidationSeverity.Allow };
	}

	private isSunscreenMaterial(type: string): boolean {
		return type === MaterialType.Sunscreen;
	}

	private canUseSpringBalancedBySize({ width, height }: Size): boolean {
		return width >= 5200 && height >= 2750;
	}

	private sizeValidator(blind: BlindData): PartValidationResult {
		const valid = !this.isShop || !blind.setup.size?.extra_size;
		return { valid, severity: valid ? PartValidationSeverity.Allow : PartValidationSeverity.Block };
	}
}
